import type { DataSources } from '../domain/sources/sources.js';
import type { DashboardView, ClientOnMap, DashboardProvenance, DashboardFilters, AgentOption } from '../domain/entities/DashboardView.js';
import type { Period } from '../domain/entities/KPI.js';
import type { Season } from '../domain/entities/Season.js';
import type { Agent } from '../domain/entities/Agent.js';
import { classifyClient } from './clientStatus.js';
import { evaluateAchievements, projectBonus } from './gamification.js';
import { buildProductMix, buildSeasonalLineMixes } from './productMix.js';
import {
  ALL_AGENTS,
  clientsForAgent, distinctLines, distinctCategories,
  buildClientRanking, buildProductRanking, buildWeeklyEvolution,
  computeAgentSeasonalKPIs,
} from './seasonalAnalytics.js';

export interface GetAgentDashboardInput {
  agentId: string;
  period: Period;
  /** Viewer role. 'admin' can see all agents aggregated and switch to any
   * single agent via `viewAgentId`. 'agent' (default) is locked to `agentId`. */
  role?: 'agent' | 'admin';
  /** For admins: which agent to view. Use the ALL_AGENTS sentinel ("__all__")
   * for the aggregated view. Ignored for non-admins (forced to `agentId`). */
  viewAgentId?: string;
  /** Season scope. If omitted the most recent available season is picked. */
  season?: Season;
  /** Optional filters (line / category). */
  filters?: Partial<Omit<DashboardFilters, 'season'>>;
  /** How many "red" clients the agent reactivated this month. Default 0. */
  reactivatedReds?: number;
}

/**
 * Single use case the UI calls. Composes every source into a ready-to-render
 * DashboardView. Per-data provenance flows through so the UI can show "demo"
 * badges per block.
 *
 * Source-failure handling: any source error rethrows — the UI layer wraps the
 * call in TanStack Query for retry/cache/fallback to last known good state.
 */
export async function getAgentDashboard(
  sources: DataSources,
  input: GetAgentDashboardInput,
  now: Date = new Date(),
): Promise<DashboardView> {
  const { agentId, period, reactivatedReds = 0 } = input;
  const role = input.role ?? 'agent';

  // Resolve which agent's data to show. Admins can request the aggregated view
  // (ALL_AGENTS) or any single agent; regular agents are locked to themselves.
  const effectiveAgentId: string =
    role === 'admin' ? (input.viewAgentId ?? ALL_AGENTS) : agentId;
  const isAllAgents = effectiveAgentId === ALL_AGENTS;

  const [headerAgent, orders, allClients, rules, rawAvailable, actuals, targets, agentDirectory] =
    await Promise.all([
      isAllAgents
        ? Promise.resolve<Agent>({ id: ALL_AGENTS, email: '', name: 'Todos los agentes', role: 'admin' })
        : sources.agent.getAgent(effectiveAgentId),
      // Orders feed the period-relative product mix + map classification. For
      // the aggregate view we skip them (the seasonal dataset drives everything
      // that matters); an empty list degrades gracefully.
      isAllAgents ? Promise.resolve([]) : sources.orders.getOrders(effectiveAgentId),
      sources.clients.getClients(effectiveAgentId),
      sources.gamification.getRules(),
      sources.seasonal.listSeasons(),
      sources.seasonal.getActuals(),
      sources.seasonal.getTargets(),
      role === 'admin' ? sources.agent.listAgents() : Promise.resolve([]),
    ]);
  const agent = headerAgent;

  // Product decision: only the currently-relevant campaigns surface in the
  // season selector — SS25, SS26, AW26 and AW27 once it arrives in the data.
  // Older seasons stay in the underlying dataset (and are still used as N-1
  // comparison) but never appear as a choice.
  const SEASON_WHITELIST: Season[] = ['SS25', 'SS26', 'AW26', 'AW27'];
  const availableSeasons = SEASON_WHITELIST.filter((s) => rawAvailable.includes(s));

  // Pick a default season: the most recent of the whitelist that has data.
  // Falls back to the most recent raw season if the whitelist intersection
  // ends up empty (defensive — shouldn't happen with current data).
  const season: Season =
    input.season && availableSeasons.includes(input.season)
      ? input.season
      : (availableSeasons[availableSeasons.length - 1]
        ?? rawAvailable[rawAvailable.length - 1]
        ?? 'SS26');

  const filters: DashboardFilters = {
    season,
    ...(input.filters?.line ? { line: input.filters.line } : {}),
    ...(input.filters?.category ? { category: input.filters.category } : {}),
  };

  // Filter the client master to the (effective) agent's portfolio. For the
  // admin aggregate view this returns every client.
  const portfolio = clientsForAgent(actuals, effectiveAgentId);
  const myClients = allClients.filter((c) => portfolio.has(c.id));

  // Available filter values are derived from the effective agent's history.
  const availableLines = distinctLines(actuals, effectiveAgentId);
  const availableCategories = distinctCategories(actuals, effectiveAgentId);

  // Season-scoped KPIs derived from the multi-season dataset (vs LY + target).
  const kpis = {
    period,
    ...computeAgentSeasonalKPIs(actuals, targets, effectiveAgentId, season, filters),
  };

  // Period-relative aggregations for the gamification context (uses orders).
  const periodMix = buildProductMix(orders, period, now);
  // Season-scoped line mixes for the comparison donuts (Current / LY / Target).
  const seasonalLineMixes = buildSeasonalLineMixes(actuals, targets, effectiveAgentId, season, filters);
  const productMix = { ...periodMix, ...seasonalLineMixes };
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).getTime();
  const ordersThisMonth = orders.filter((o) => new Date(o.date).getTime() >= monthStart);

  // Rankings & weekly evolution are season-scoped.
  const clientRanking = buildClientRanking(actuals, targets, allClients, effectiveAgentId, season, filters);
  const productRanking = buildProductRanking(actuals, effectiveAgentId, season, filters);
  const weeklyEvolution = buildWeeklyEvolution(actuals, targets, effectiveAgentId, season, filters);

  // Annual-target-per-client estimate for the map status classification.
  const totalTargetEUR = kpis.revenue.target || kpis.revenue.previousYear || 1;
  const annualPerClient = myClients.length > 0 ? totalTargetEUR / myClients.length : 0;
  const clientsOnMap: ClientOnMap[] = myClients.map((c) => ({
    ...c,
    status: classifyClient(c, orders, annualPerClient, now),
  }));

  const achievements = evaluateAchievements(kpis, rules, {
    reactivatedReds,
    productMix,
    ordersThisMonth,
  });
  const bonus = projectBonus(kpis, rules, now);

  const provenance: DashboardProvenance = {
    agent: sources.agent.provenance,
    orders: sources.orders.provenance,
    clients: sources.clients.provenance,
    targets: sources.seasonal.provenance,
    gamification: sources.gamification.provenance,
    products: sources.products.provenance,
  };

  const availableAgents: AgentOption[] = agentDirectory
    .map((a) => ({ id: a.id, name: a.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    agent, period, isAllAgents, viewAgentId: effectiveAgentId, availableAgents,
    season, availableSeasons, availableLines, availableCategories,
    filters, kpis, clients: clientsOnMap, achievements, bonus, productMix,
    clientRanking, productRanking, weeklyEvolution, provenance,
  };
}
